/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { FormattedMessage } from '@modules/i18n'
import type { RoutableBreadcrumb } from '@modules/ui-types'
import type { ProjectBreadcrumbParams } from '@modules/ui-types/projects'
import { getDisplayProjectId } from '@modules/project-lib/display'
import {
  projectCpsUrl,
  projectNetworkSecurityUrl,
  projectUrl,
  projectsUrl,
} from '@modules/project-user-lib/urls'

export const projectsCrumbs = (): RoutableBreadcrumb[] => [
  {
    to: projectsUrl(),
    text: (
      <FormattedMessage id='crumb-builder.projects-crumb' defaultMessage='Serverless projects' />
    ),
  },
]

export const projectCrumbs = ({
  projectType,
  projectId,
  projectName,
}: ProjectBreadcrumbParams): RoutableBreadcrumb[] => [
  ...projectsCrumbs(),

  {
    to: projectUrl(projectType, projectId),
    text: projectName || getDisplayProjectId(projectId),
  },
]

export const projectNetworkSecurityCrumbs = ({
  projectType,
  projectId,
  projectName,
}: ProjectBreadcrumbParams): RoutableBreadcrumb[] => [
  ...projectCrumbs({ projectType, projectId, projectName }),

  {
    to: projectNetworkSecurityUrl(projectType, projectId),
    text: (
      <FormattedMessage
        id='crumb-builder.project-network-security-crumb'
        defaultMessage='Network security'
      />
    ),
  },
]

export const projectCpsCrumbs = ({
  projectType,
  projectId,
  projectName,
}: ProjectBreadcrumbParams): RoutableBreadcrumb[] => [
  ...projectCrumbs({ projectType, projectId, projectName }),

  {
    to: projectCpsUrl(projectType, projectId),
    text: (
      <FormattedMessage
        id='crumb-builder.project-cps-crumb'
        defaultMessage='Cross-project search'
      />
    ),
  },
]

export const projectCpsWizardCrumbs = ({
  projectType,
  projectId,
  projectName,
}: ProjectBreadcrumbParams): RoutableBreadcrumb[] => [
  ...projectCpsCrumbs({ projectType, projectId, projectName }),
  {
    text: (
      <FormattedMessage
        id='crumb-builder.project-cps-wizard-crumb'
        defaultMessage='Link projects'
      />
    ),
  },
]
